import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  FileDiff, 
  Sparkles, 
  Download, 
  Copy, 
  Check,
  DollarSign,
  Clock,
  FileText,
  Send,
  Receipt,
  CheckCircle,
  XCircle,
  Mail,
  RefreshCw,
  ArrowLeft,
  Trash2,
  Zap,
  Plus,
  BookTemplate
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-500/20 text-green-300 border-green-500/30', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-500/20 text-red-300 border-red-500/30', icon: XCircle }
};

export default function ChangeOrderGenerator({ user, usage, refreshUsage }) {
  const isOutOfCredits =
    user?.tier === "free" &&
    !usage?.is_demo &&
    typeof usage?.llm_credits_remaining === "number" &&
    usage.llm_credits_remaining <= 0;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [templates, setTemplates] = useState({ templates: [], default_templates: [] });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);
  
  // View mode: 'create' or 'list' or 'detail' or 'templates'
  const [viewMode, setViewMode] = useState('create');
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Send modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  
  // Template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    reason: '',
    scope_diff: '',
    pricing_type: 'fixed',
    pricing_adjustment: 0,
    pricing_percentage: 0,
    timeline_adjustment_days: 0
  });
  
  const [formData, setFormData] = useState({
    project_id: '',
    reason: '',
    scope_diff: '',
    pricing_adjustment: 0,
    timeline_adjustment_days: 0
  });

  useEffect(() => {
    loadData();
    
    // Check URL params for actions
    const id = searchParams.get('id');
    const action = searchParams.get('action');
    if (id && action === 'send') {
      loadChangeOrder(id).then(() => setShowSendModal(true));
    }
  }, [searchParams]);

  async function loadData() {
    try {
      const [projectsRes, ordersRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/projects`, { credentials: 'include' }),
        fetch(`${API_URL}/api/change-orders`, { credentials: 'include' }),
        fetch(`${API_URL}/api/change-orders/templates/list`, { credentials: 'include' })
      ]);
      
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        const projectList = Array.isArray(data) ? data : (data.projects || []);
        setProjects(projectList);
      }
      
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setChangeOrders(data.change_orders || []);
      }
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadChangeOrder(id) {
    try {
      const response = await fetch(`${API_URL}/api/change-orders/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedOrder(data.change_order);
        setResult({ markdown: data.change_order.generated_md, _id: id });
        
        // Pre-fill email if client exists
        if (data.client?.email) {
          setSendEmail(data.client.email);
        }
        
        return data;
      }
    } catch (error) {
      console.error('Failed to load change order:', error);
    }
  }

  async function handleGenerate() {
    if (isOutOfCredits) {
      navigate('/billing');
      return;
    }
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for the change');
      return;
    }
    if (!formData.scope_diff.trim()) {
      toast.error('Please describe the scope changes');
      return;
    }

    try {
      setGenerating(true);
      const response = await fetch(`${API_URL}/api/change-orders/projects/${formData.project_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reason: formData.reason,
          scope_diff: formData.scope_diff,
          pricing_adjustment: parseFloat(formData.pricing_adjustment) || 0,
          timeline_adjustment_days: parseInt(formData.timeline_adjustment_days) || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setSelectedOrder(data.change_order);
        toast.success('Change order generated!');
        refreshUsage?.();
        loadData(); // Refresh list
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to generate change order');
      }
    } catch (error) {
      toast.error('Failed to generate change order');
    } finally {
      setGenerating(false);
    }
  }

  function handleCopy() {
    if (result?.markdown) {
      navigator.clipboard.writeText(result.markdown);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function handleDownloadPDF() {
    const id = result?._id || selectedOrder?._id;
    if (!id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/change-orders/${id}/export-pdf`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Change_Order_${id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF downloaded!');
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  }

  async function handleSend() {
    const id = result?._id || selectedOrder?._id;
    if (!id || !sendEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSending(true);
      const response = await fetch(`${API_URL}/api/change-orders/${id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: sendEmail })
      });

      if (response.ok) {
        toast.success(`Change order sent to ${sendEmail}!`);
        setShowSendModal(false);
        loadData();
        if (selectedOrder) {
          setSelectedOrder({ ...selectedOrder, status: 'sent', sent_to_email: sendEmail });
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send change order');
    } finally {
      setSending(false);
    }
  }

  async function handleUpdateStatus(status) {
    const id = selectedOrder?._id;
    if (!id) return;

    try {
      const response = await fetch(`${API_URL}/api/change-orders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Status updated to ${status}`);
        setSelectedOrder({ ...selectedOrder, status });
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  }

  async function handleCreateInvoice() {
    const id = selectedOrder?._id;
    if (!id) return;

    try {
      const response = await fetch(`${API_URL}/api/change-orders/${id}/create-invoice`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Invoice created!');
        navigate(`/invoices?id=${data.invoice_id}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  }

  async function handleDelete(id) {
    if (!confirm('Are you sure you want to delete this change order?')) return;

    try {
      const response = await fetch(`${API_URL}/api/change-orders/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Change order deleted');
        loadData();
        if (selectedOrder?._id === id) {
          setSelectedOrder(null);
          setResult(null);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  }

  // Template functions
  async function handleUseTemplate(template) {
    if (isOutOfCredits) {
      navigate('/billing');
      return;
    }
    if (!formData.project_id) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setGenerating(true);
      const response = await fetch(`${API_URL}/api/change-orders/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: formData.project_id,
          template_id: template._id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setSelectedOrder(data.change_order);
        toast.success('Change order created from template!');
        refreshUsage?.();
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create from template');
      }
    } catch (error) {
      toast.error('Failed to create from template');
    } finally {
      setGenerating(false);
    }
  }

  async function handleSaveTemplate() {
    if (!newTemplate.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/change-orders/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        toast.success('Template saved!');
        setShowTemplateModal(false);
        setNewTemplate({
          name: '',
          description: '',
          reason: '',
          scope_diff: '',
          pricing_type: 'fixed',
          pricing_adjustment: 0,
          pricing_percentage: 0,
          timeline_adjustment_days: 0
        });
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  }

  async function handleDeleteTemplate(templateId) {
    if (!confirm('Delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/change-orders/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Template deleted');
        loadData();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  }

  const allTemplates = [...(templates.default_templates || []), ...(templates.templates || [])];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="change-order-generator">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileDiff className="w-6 h-6 text-indigo-400" />
            Change Order Generator
          </h1>
          <p className="text-slate-400 mt-1">
            Generate professional change orders for scope changes using AI
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => { setViewMode('create'); setSelectedOrder(null); setResult(null); }}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'create' ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Create New
          </button>
          <button
            onClick={() => setViewMode('templates')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'templates' ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-1" />
            Templates
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'list' ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            View All ({changeOrders.length})
          </button>
        </div>
      </div>

      {viewMode === 'templates' ? (
        /* Templates View */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Quick Templates</h2>
            <Button
              onClick={() => setShowTemplateModal(true)}
              variant="outline"
              className="border-indigo-500 text-indigo-400 hover:bg-indigo-500/20"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          {/* Project Selector for Templates */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="text-slate-300 whitespace-nowrap">Select Project:</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(v) => setFormData({ ...formData, project_id: v })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white max-w-xs">
                    <SelectValue placeholder="Choose project first" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.project_id && (
                  <span className="text-amber-400 text-sm">← Required to use templates</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Template Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allTemplates.map((template) => (
              <Card
                key={template._id}
                className={`bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 transition-all ${
                  !formData.project_id ? 'opacity-60' : ''
                }`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-amber-400" />
                        <h3 className="font-semibold text-white">{template.name}</h3>
                        {template.is_default && (
                          <Badge variant="outline" className="bg-slate-700 text-slate-300 border-slate-600 text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mb-3">{template.description}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-400">
                          {template.pricing_type === 'percentage' 
                            ? `+${template.pricing_percentage}%` 
                            : `+$${template.pricing_adjustment}`}
                        </span>
                        <span className="text-slate-500">
                          {template.timeline_adjustment_days >= 0 ? '+' : ''}{template.timeline_adjustment_days} days
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!template.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template._id); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => handleUseTemplate(template)}
                        disabled={!formData.project_id || generating}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white"
                        size="sm"
                      >
                        {generating ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-1" />
                            Use
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : viewMode === 'list' ? (
        /* Change Orders List */
        <div className="space-y-4">
          {changeOrders.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <FileDiff className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">No change orders yet</p>
                <Button 
                  onClick={() => setViewMode('create')}
                  className="mt-4 bg-indigo-500 hover:bg-indigo-600"
                >
                  Create Your First
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {changeOrders.map((co) => {
                const StatusIcon = STATUS_CONFIG[co.status]?.icon || FileText;
                const project = projects.find(p => p.id === co.project_id);
                
                return (
                  <Card 
                    key={co._id} 
                    className="bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 transition-colors cursor-pointer"
                    onClick={() => { loadChangeOrder(co._id); setViewMode('detail'); }}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-slate-700 rounded-lg">
                            <FileDiff className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">{co.reason}</h3>
                            <p className="text-sm text-slate-400">
                              {project?.title || 'Unknown Project'} • {new Date(co.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-green-400 font-semibold">+${co.pricing_adjustment?.toLocaleString() || 0}</p>
                            <p className="text-xs text-slate-500">+{co.timeline_adjustment_days || 0} days</p>
                          </div>
                          <Badge variant="outline" className={STATUS_CONFIG[co.status]?.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_CONFIG[co.status]?.label || co.status}
                          </Badge>
                          {co.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete(co._id); }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ) : viewMode === 'detail' && selectedOrder ? (
        /* Detail View */
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => setViewMode('list')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Details Card */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-white">Change Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-400">Status</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className={STATUS_CONFIG[selectedOrder.status]?.color}>
                      {STATUS_CONFIG[selectedOrder.status]?.label}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-400">Project</Label>
                  <p className="text-white mt-1">
                    {projects.find(p => p.id === selectedOrder.project_id)?.title || 'Unknown'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-slate-400">Reason</Label>
                  <p className="text-white mt-1">{selectedOrder.reason}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400">Price Adjustment</Label>
                    <p className="text-green-400 font-semibold mt-1">
                      +${selectedOrder.pricing_adjustment?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-400">Timeline</Label>
                    <p className="text-white mt-1">+{selectedOrder.timeline_adjustment_days || 0} days</p>
                  </div>
                </div>

                {selectedOrder.sent_to_email && (
                  <div>
                    <Label className="text-slate-400">Sent To</Label>
                    <p className="text-white mt-1">{selectedOrder.sent_to_email}</p>
                  </div>
                )}

                {selectedOrder.invoice_id && (
                  <div>
                    <Label className="text-slate-400">Invoice</Label>
                    <Button
                      variant="link"
                      className="text-indigo-400 p-0 h-auto mt-1"
                      onClick={() => navigate(`/invoices?id=${selectedOrder.invoice_id}`)}
                    >
                      View Invoice →
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2 pt-4 border-t border-slate-700">
                  <Button
                    onClick={handleDownloadPDF}
                    variant="outline"
                    className="w-full border-slate-600 text-white hover:bg-slate-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  {selectedOrder.status === 'draft' && (
                    <Button
                      onClick={() => setShowSendModal(true)}
                      className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send to Client
                    </Button>
                  )}
                  
                  {selectedOrder.status === 'sent' && (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleUpdateStatus('accepted')}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accepted
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus('declined')}
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-500/20"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Declined
                      </Button>
                    </div>
                  )}
                  
                  {selectedOrder.status === 'accepted' && !selectedOrder.invoice_id && (
                    <Button
                      onClick={handleCreateInvoice}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      <Receipt className="w-4 h-4 mr-2" />
                      Create Invoice
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Document Preview */}
            <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-400" />
                    Document Preview
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="border-slate-600 text-white hover:bg-slate-700"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-900/50 rounded-lg p-4 max-h-[600px] overflow-auto">
                  <pre className="text-slate-300 text-sm whitespace-pre-wrap font-mono">
                    {selectedOrder.generated_md}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        /* Create Form */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Change Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Project *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(v) => setFormData({ ...formData, project_id: v })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Reason for Change *</Label>
                <Input
                  placeholder="e.g., Client requested additional features"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-slate-300">Scope Changes *</Label>
                <Textarea
                  placeholder="Describe what's being added, changed, or removed from the original scope..."
                  value={formData.scope_diff}
                  onChange={(e) => setFormData({ ...formData, scope_diff: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1 min-h-[120px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    Price Adjustment
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.pricing_adjustment}
                    onChange={(e) => setFormData({ ...formData, pricing_adjustment: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Days Added
                  </Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.timeline_adjustment_days}
                    onChange={(e) => setFormData({ ...formData, timeline_adjustment_days: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className={`w-full text-white ${
                  isOutOfCredits
                    ? "bg-[#B23A48] hover:bg-[#9c2e3b]"
                    : "bg-indigo-500 hover:bg-indigo-600"
                }`}
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isOutOfCredits ? "Upgrade to continue" : "Generate Change Order"}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Result */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-400" />
                  Generated Document
                </CardTitle>
                {result && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopy}
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPDF}
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowSendModal(true)}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Send
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="bg-slate-900/50 rounded-lg p-4 max-h-[500px] overflow-auto">
                  <pre className="text-slate-300 text-sm whitespace-pre-wrap font-mono">
                    {result.markdown}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileDiff className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No change order generated yet</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Fill in the form and click generate
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-400" />
              Send Change Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Send the change order to your client with a PDF attachment
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-slate-300">Client Email</Label>
            <Input
              type="email"
              placeholder="client@example.com"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSendModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSend}
              disabled={sending || !sendEmail}
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send with PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-amber-400" />
              Create Change Order Template
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Save commonly used change orders as templates for quick access
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Template Name *</Label>
              <Input
                placeholder="e.g., Rush Fee (+20%)"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Description</Label>
              <Input
                placeholder="Brief description of when to use this template"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Default Reason</Label>
              <Input
                placeholder="Reason that will be pre-filled"
                value={newTemplate.reason}
                onChange={(e) => setNewTemplate({ ...newTemplate, reason: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Scope Change Description</Label>
              <Textarea
                placeholder="Default scope change text..."
                value={newTemplate.scope_diff}
                onChange={(e) => setNewTemplate({ ...newTemplate, scope_diff: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1 min-h-[80px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Pricing Type</Label>
                <Select
                  value={newTemplate.pricing_type}
                  onValueChange={(v) => setNewTemplate({ ...newTemplate, pricing_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="fixed" className="text-white">Fixed Amount ($)</SelectItem>
                    <SelectItem value="percentage" className="text-white">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">
                  {newTemplate.pricing_type === 'percentage' ? 'Percentage' : 'Amount'}
                </Label>
                <Input
                  type="number"
                  step={newTemplate.pricing_type === 'percentage' ? '1' : '0.01'}
                  placeholder={newTemplate.pricing_type === 'percentage' ? '20' : '150.00'}
                  value={newTemplate.pricing_type === 'percentage' ? newTemplate.pricing_percentage : newTemplate.pricing_adjustment}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    if (newTemplate.pricing_type === 'percentage') {
                      setNewTemplate({ ...newTemplate, pricing_percentage: val });
                    } else {
                      setNewTemplate({ ...newTemplate, pricing_adjustment: val });
                    }
                  }}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Timeline Adjustment (days)</Label>
              <Input
                type="number"
                placeholder="0"
                value={newTemplate.timeline_adjustment_days}
                onChange={(e) => setNewTemplate({ ...newTemplate, timeline_adjustment_days: parseInt(e.target.value) || 0 })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Use negative for rush orders (e.g., -5 for 5 days faster)</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowTemplateModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
